import { useEffect, useState } from "react";
import PropTypes from "prop-types";
// @mui
import {
  Grid,
  Stack,
  Button,
  Drawer,
  Divider,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  FormHelperText,
  CircularProgress,
  TextField,
  IconButton,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";

// components
import { Close, Search, CheckCircle, Cancel } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import { useGetPlansQuery } from "../../../redux/services/plans.service";
import {
  useCheckManualSubscriptionUserMutation,
  useCreateManualSubscriptionMutation,
} from "../../../redux/services/subscription.service";
import Scrollbar from "../../../components/scrollbar";
import CustomDialog from "../../../components/dialog/CustomDialog";
import {
  ManualSubscriptionCheckSchema,
  ManualSubscriptionSchema,
} from "../../../forms-schema/form.schema";

ManualSubscriptionForm.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

const getStatusColor = (status) => {
  switch (status) {
    case "active":
      return "success";
    case "expired":
      return "error";
    case "cancelled":
      return "warning";
    case "authenticated":
      return "info";
    default:
      return "default";
  }
};

export default function ManualSubscriptionForm({ open, onClose }) {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Check User, 2: Create Subscription
  const [userData, setUserData] = useState(null);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);

  // Form for step 1 - Check User
  const {
    handleSubmit: handleSubmitCheck,
    register: registerCheck,
    formState: { errors: errorsCheck },
    reset: resetCheck,
    watch: watchCheck,
  } = useForm({
    resolver: yupResolver(ManualSubscriptionCheckSchema),
  });

  // Form for step 2 - Create Subscription
  const {
    handleSubmit: handleSubmitCreate,
    register: registerCreate,
    formState: { errors: errorsCreate },
    reset: resetCreate,
    watch: watchCreate,
    setValue: setValueCreate,
    control,
  } = useForm({
    resolver: yupResolver(ManualSubscriptionSchema),
  });

  useEffect(() => {
    resetCheck({ contactNumber: "" });
    resetCreate({
      contactNumber: "",
      planId: "",
      startDate: undefined,
      endDate: undefined,
      eventPlace: "",
      tag: "",
    });
    setStep(1);
    setUserData(null);
  }, [open, resetCheck, resetCreate]);

  const {
    isLoading: isPlansLoading,
    isFetching: isPlansFetching,
    data: plans,
  } = useGetPlansQuery(null, {
    skip: !open,
  });

  const [checkManualSubscriptionUser, { isLoading: isCheckingUser }] =
    useCheckManualSubscriptionUserMutation();
  const [createManualSubscription, { isLoading: isCreatingSubscription }] =
    useCreateManualSubscriptionMutation();

  // Step 1: Check User
  const handleCheckUser = async (values) => {
    try {
      const result = await checkManualSubscriptionUser({
        contactNumber: values.contactNumber,
      });

      if (result?.data?.success) {
        setUserData(result.data.data);
        setValueCreate("contactNumber", values.contactNumber);
        setStep(2);
        enqueueSnackbar("User found successfully", {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
        });
      } else if (result?.error) {
        const errorData = result?.error?.data;
        enqueueSnackbar(errorData?.message || "User not found", {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
        });
      }
    } catch (error) {
      console.error("Error checking user:", error);
      enqueueSnackbar("An error occurred. Please try again.", {
        variant: "error",
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
      });
    }
  };

  // Step 2: Create Subscription
  const handleCreateSubscription = async (values) => {
    setLoading(true);
    try {
      const payload = {
        contactNumber: values.contactNumber,
        planId: values.planId,
        ...(values.startDate && {
          startDate: moment(values.startDate).format("YYYY-MM-DD"),
        }),
        ...(values.endDate && {
          endDate: moment(values.endDate).format("YYYY-MM-DD"),
        }),
        ...(values.eventPlace && { eventPlace: values.eventPlace }),
        ...(values.tag && { tag: values.tag }),
      };

      const result = await createManualSubscription(payload);

      if (result?.data?.success) {
        enqueueSnackbar(
          result?.data?.message || "Manual subscription created successfully",
          {
            variant: "success",
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
          },
        );
        resetCheck();
        resetCreate();
        setStep(1);
        setUserData(null);
        setOpenConfirmationDialog(false);
        onClose();
      } else if (result?.error) {
        const errorData = result?.error?.data;
        enqueueSnackbar(errorData?.message || "Something went wrong", {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
        });
        setOpenConfirmationDialog(false);
      }
    } catch (error) {
      console.error("Error creating subscription:", error);
      enqueueSnackbar("An error occurred. Please try again.", {
        variant: "error",
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenConfirmationDialog = () => {
    setOpenConfirmationDialog(true);
  };

  const handleBack = () => {
    setStep(1);
    setUserData(null);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 480 },
            maxWidth: "100vw",
            border: "none",
            overflow: "hidden",
          },
        }}
      >
        <Scrollbar>
          <Stack spacing={3} sx={{ p: { xs: 2, sm: 3 } }}>
            <div>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    pr: 1,
                    lineHeight: 1.3,
                    overflowWrap: "anywhere",
                  }}
                >
                  Manual Subscription {step === 1 ? "- Check User" : "- Create"}
                </Typography>
                <IconButton onClick={onClose}>
                  <Close />
                </IconButton>
              </Box>
              <Divider />
            </div>

            {/* Step 1: Check User */}
            {step === 1 && (
              <Stack spacing={3}>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  <Typography variant="caption">
                    Enter the user's contact number to check their subscription
                    history before creating a manual subscription.
                  </Typography>
                </Alert>

                <TextField
                  {...registerCheck("contactNumber")}
                  label="Contact Number *"
                  placeholder="Enter 10 digit mobile number"
                  fullWidth
                  error={Boolean(errorsCheck.contactNumber)}
                  helperText={errorsCheck.contactNumber?.message}
                  inputProps={{
                    maxLength: 10,
                    "data-testid": "contactNumber",
                  }}
                />

                <LoadingButton
                  variant="contained"
                  startIcon={<Search />}
                  onClick={handleSubmitCheck(handleCheckUser)}
                  loading={isCheckingUser}
                  fullWidth
                >
                  Check User
                </LoadingButton>
              </Stack>
            )}

            {/* Step 2: User Info & Create Subscription */}
            {step === 2 && userData && (
              <Stack spacing={3}>
                {/* User Info Card */}
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    User Information
                  </Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Name
                      </Typography>
                      <Typography variant="body2">
                        {userData?.user?.firstName} {userData?.user?.lastName}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Contact
                      </Typography>
                      <Typography variant="body2">
                        {userData?.user?.contactNumber}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body2" noWrap>
                        {userData?.user?.email || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Subscribed
                      </Typography>
                      <Box>
                        {userData?.user?.isSubscribed ? (
                          <Chip
                            icon={<CheckCircle />}
                            label="Yes"
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            icon={<Cancel />}
                            label="No"
                            color="error"
                            size="small"
                          />
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Document Wallet Info */}
                {userData?.documentWallet && (
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Document Wallet
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />
                    <Grid container spacing={1}>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">
                          Pages Used
                        </Typography>
                        <Typography variant="body2">
                          {userData?.documentWallet?.pagesUsed} /{" "}
                          {userData?.documentWallet?.totalPagesLimit}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">
                          Active
                        </Typography>
                        <Typography variant="body2">
                          {userData?.documentWallet?.isActive ? "Yes" : "No"}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">
                          Free
                        </Typography>
                        <Typography variant="body2">
                          {userData?.documentWallet?.isFree ? "Yes" : "No"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                )}

                {/* Subscription History */}
                {userData?.subscriptions?.length > 0 && (
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Subscription History ({userData?.totalSubscriptions})
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />
                    <TableContainer sx={{ maxHeight: 200 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Plan</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>End Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userData?.subscriptions?.map((sub) => (
                            <TableRow key={sub.id}>
                              <TableCell>{sub.planName || "-"}</TableCell>
                              <TableCell>
                                <Chip
                                  label={sub.subscriptionType}
                                  size="small"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={sub.status}
                                  size="small"
                                  color={getStatusColor(sub.status)}
                                />
                              </TableCell>
                              <TableCell>
                                {moment(sub.subscriptionEnd).format(
                                  "DD/MM/YYYY",
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}

                {userData?.subscriptions?.length === 0 && (
                  <Alert severity="info">
                    No previous subscriptions found for this user.
                  </Alert>
                )}

                <Divider>
                  <Typography variant="caption" color="text.secondary">
                    Create New Subscription
                  </Typography>
                </Divider>

                {/* Date Info Alert */}
                <Alert severity="info" sx={{ py: 0.5 }}>
                  <Typography variant="caption">
                    <strong>Start Date</strong> and <strong>End Date</strong>{" "}
                    are optional. If not provided, the subscription duration
                    will be automatically calculated based on the selected
                    plan's default duration.
                  </Typography>
                </Alert>

                {/* Plan Select */}
                <FormControl fullWidth>
                  <InputLabel
                    id="plan-label"
                    error={Boolean(errorsCreate.planId)}
                  >
                    Plan *
                  </InputLabel>
                  <Select
                    {...registerCreate("planId", { required: true })}
                    labelId="plan-label"
                    id="plan-select"
                    label="Plan *"
                    inputProps={{ "data-testid": "planId" }}
                    error={Boolean(errorsCreate.planId)}
                    value={watchCreate("planId") || ""}
                  >
                    {isPlansLoading || isPlansFetching ? (
                      <MenuItem
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : (
                      plans?.data
                        ?.toSorted((a, b) => a?.name?.localeCompare(b?.name))
                        ?.map((plan) => (
                          <MenuItem key={plan?.id} value={plan?.id}>
                            {plan?.name}
                          </MenuItem>
                        ))
                    )}
                  </Select>
                  <FormHelperText error={Boolean(errorsCreate.planId)}>
                    {errorsCreate.planId?.message}
                  </FormHelperText>
                </FormControl>

                {/* Start Date */}
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <Controller
                    name="startDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        label="Start Date"
                        format="DD/MM/YYYY"
                        value={field.value ? moment(field.value) : null}
                        onChange={(date) => {
                          setValueCreate(
                            "startDate",
                            date ? date.toDate() : undefined,
                          );
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: Boolean(errorsCreate.startDate),
                            helperText: errorsCreate.startDate?.message,
                          },
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>

                {/* End Date */}
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <Controller
                    name="endDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        label="End Date"
                        format="DD/MM/YYYY"
                        value={field.value ? moment(field.value) : null}
                        onChange={(date) => {
                          setValueCreate(
                            "endDate",
                            date ? date.toDate() : null,
                          );
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: Boolean(errorsCreate.endDate),
                            helperText: errorsCreate.endDate?.message,
                          },
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>

                {/* Event Place */}
                <TextField
                  {...registerCreate("eventPlace")}
                  label="Event Place"
                  fullWidth
                  error={Boolean(errorsCreate.eventPlace)}
                  helperText={errorsCreate.eventPlace?.message}
                />

                {/* Tag */}
                <TextField
                  {...registerCreate("tag")}
                  label="Tag"
                  fullWidth
                  error={Boolean(errorsCreate.tag)}
                  helperText={errorsCreate.tag?.message}
                />

                <Stack direction="row" spacing={2}>
                  <Button variant="outlined" onClick={handleBack} fullWidth>
                    Back
                  </Button>
                  <LoadingButton
                    variant="contained"
                    onClick={handleSubmitCreate(handleOpenConfirmationDialog)}
                    loading={isCreatingSubscription || loading}
                    fullWidth
                  >
                    Create Subscription
                  </LoadingButton>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Scrollbar>
      </Drawer>

      {/* Confirmation Dialog */}
      <CustomDialog
        open={openConfirmationDialog}
        onClose={() => setOpenConfirmationDialog(false)}
        title="Confirm Manual Subscription"
        description={
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Are you sure you want to create a manual subscription for:
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body2">
                <strong>User:</strong> {userData?.user?.firstName}{" "}
                {userData?.user?.lastName}
              </Typography>
              <Typography variant="body2">
                <strong>Contact:</strong> {userData?.user?.contactNumber}
              </Typography>
              <Typography variant="body2">
                <strong>Plan:</strong>{" "}
                {plans?.data?.find((p) => p.id === watchCreate("planId"))
                  ?.name || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Start Date:</strong>{" "}
                {watchCreate("startDate")
                  ? moment(watchCreate("startDate")).format("DD/MM/YYYY")
                  : "-"}
              </Typography>
              <Typography variant="body2">
                <strong>End Date:</strong>{" "}
                {watchCreate("endDate")
                  ? moment(watchCreate("endDate")).format("DD/MM/YYYY")
                  : "-"}
              </Typography>
            </Box>
          </>
        }
        onConfirm={handleSubmitCreate(handleCreateSubscription)}
        isLoading={loading || isCreatingSubscription}
      />
    </>
  );
}
